import React, { useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Loader } from 'ncoded-component-library';
import TransWithNda from 'components/TransWithNda';
import useCallbackRef from 'hooks/useCallbackRef';
import useAgreementTemplateQuery from 'hooks/useAgreementTemplateQuery';
import DocumentLockedIcon from 'icons/DocumentLocked.icon';
import PlusIcon from 'icons/Plus.icon';
import { getFormattedDate } from 'dateUtils';
import { getIsAgreementTemplateActive } from 'agreementUtils';
import ListingContext from '../../../../../../providers/Listing/Listing.context';
import AddCustomNdaModal from '../AddCustomNdaModal';
import {
  AgreementPageLocationState,
  getAgreementSetupRoute,
  getAgreementViewRoute,
} from '../../../../../../utils';

import './NativeAgreementCard.styles.scss';

type NativeAgreementCardProps = {
  className?: string;
};

const NativeAgreementCard: React.FC<NativeAgreementCardProps> = (props) => {
  const { className } = props;

  const {
    areActionsDisabled,
    listing: { _id: listingId },
    dealRoom: { _id: dealRoomId },
  } = useContext(ListingContext);

  const [chooseDocsModal, chooseDocsModalRef] = useCallbackRef();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { t } = useTranslation();

  const { data: template, isLoading } = useAgreementTemplateQuery(listingId);

  const locationState: AgreementPageLocationState = { from: pathname };

  const classes = classNames('native-agreement-card', className);

  if (isLoading)
    return (
      <div className={classes}>
        <Loader />
      </div>
    );

  if (!getIsAgreementTemplateActive(template))
    return (
      <div className={classes}>
        <p className="native-agreement-card__description">
          {t('youDontHaveNda')}
        </p>
        <Button
          type="button"
          variant="outline"
          icon={PlusIcon}
          className="native-agreement-card__set-up"
          disabled={areActionsDisabled}
          onClick={() =>
            navigate(getAgreementSetupRoute(dealRoomId), {
              state: locationState,
            })
          }
        >
          {t('uploadNdaToDataRoom')}
        </Button>
      </div>
    );

  const { originalFilename, createdAt } = template.currentVersion;

  return (
    <div className={classes}>
      <Link
        to={getAgreementViewRoute(dealRoomId)}
        state={locationState}
        className="native-agreement-card__file"
      >
        <DocumentLockedIcon />
        <div>
          <p>{originalFilename}</p>
          <p>
            {t('uploaded')} {getFormattedDate(new Date(createdAt))}
          </p>
        </div>
      </Link>

      <Button
        type="button"
        variant="link"
        className="native-agreement-card__unlock-docs"
        disabled={areActionsDisabled}
        onClick={() => chooseDocsModal?.open()}
      >
        <TransWithNda
          i18nKey="chooseDocsToUnlockAfterNDA"
          envelopeTemplateName={originalFilename}
        />
      </Button>

      <AddCustomNdaModal
        envelopeTemplateName={originalFilename}
        ref={chooseDocsModalRef}
      />
    </div>
  );
};

export default NativeAgreementCard;
